import React, { useEffect, useState } from 'react';
import './AppointmentNavigator.css';
import { Route, Redirect, RouteComponentProps } from 'react-router-dom';
import LaboratorySelection from '../pages/appointmentSteps/LaboratorySelection';
import PreselectLaboratory from '../pages/appointmentSteps/PreselectLaboratory';
import UploadPrescription from '../pages/appointmentSteps/UploadPrescription';
import UploadAndCheckPrescription from '../pages/appointmentSteps/UploadPrescriptionSelas';
import BookingCalendar from '../pages/appointmentSteps/BookingCalendar';
import SetPatient from '../pages/appointmentSteps/SetPatient';
import { IonRouterOutlet } from '@ionic/react';
import Summary from '../pages/appointmentSteps/Summary';
import ThankYou from '../pages/appointmentSteps/ThankYou';
import RegisterStep from '../pages/appointmentSteps/RegisterStep';
import { AuthenticationRootState } from '../store/reducers/authentication';
import { connect } from 'react-redux';
import TabBar from '../components/TabBar';
import SidepForm from '../components/SidepForm';
import {
  APPOINTMENT_ROUTE,
  COVID_APPOINTMENT_ROUTE,
  SIDEP_ONLY_APPOINTMENT_ROUTE
} from './AppStackNavigator';
import { StepperProps } from '../components/Stepper';
import { AppointmentsRootState } from '../store/reducers/appointments';
import { useLocation } from 'react-router-dom';
import { Laboratory } from '../models';
import SpecialityForms from '../pages/appointmentSteps/SpecialityForms';

const mapStateToProps = (
  state: {
    authentication: AuthenticationRootState;
    appointments: AppointmentsRootState;
  },
  ownProps: any
) => {
  return {
    laboratory: state.appointments.appointment.laboratory,
    user: state.authentication.user,
    isLoggedIn: !!state.authentication.accessToken
  };
};

export type AppointmentNavigatorProps = ReturnType<typeof mapStateToProps> &
  RouteComponentProps & {
    isCovid: boolean;
    isSidepOnly: boolean;
    skipSlot: boolean;
    automaticCheckin: boolean;
    isLoggedIn: boolean;
  };

const AppointmentNavigator: React.FC<AppointmentNavigatorProps> = ({
  match,
  isLoggedIn,
  history,
  isCovid = false,
  isSidepOnly = false,
  skipSlot = false,
  automaticCheckin = false,
  laboratory
}) => {
  const location = useLocation();

  const [lastSelectedLaboratory, setLastSelectedLaboratory] =
    useState<Laboratory | null>(null);

  useEffect(() => {
    if (!!laboratory) {
      setLastSelectedLaboratory(laboratory);
    }
  }, [laboratory]);

  useEffect(() => {
    if (
      !laboratory &&
      !location.pathname.includes('/laboratoire') &&
      !location.pathname.includes(SIDEP_ONLY_APPOINTMENT_ROUTE) &&
      location.pathname !== APPOINTMENT_ROUTE &&
      location.pathname !== `${APPOINTMENT_ROUTE}/` &&
      location.pathname !== COVID_APPOINTMENT_ROUTE &&
      location.pathname !== `${COVID_APPOINTMENT_ROUTE}/`
    ) {
      history.push('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const matchUrl = match.url.replace(/\/$/, '');

  return (
    <>
      <IonRouterOutlet animated={true}>
        <Route
          path={`${matchUrl}/laboratoire`}
          exact
          render={(props) => (
            <LaboratorySelection
              {...props}
              step={0}
              nextStepRouterLink={`${matchUrl}/patient`}
              isCovid={isCovid}
            />
          )}
        />
        <Route
          path={`${matchUrl}/laboratoire/:slug`}
          exact
          render={(props) => (
            <PreselectLaboratory
              {...props}
              step={0}
              nextStepRouterLink={`${matchUrl}/patient`}
            />
          )}
        />
        <Route
          path={`${matchUrl}/patient`}
          render={(props) => (
            <SetPatient
              {...props}
              step={1}
              nextStepRouterLink={`${matchUrl}/ordonnance`}
            />
          )}
        />
        <Route
          path={`${matchUrl}/ordonnance`}
          render={(props) => (
            <UploadPrescription
              {...props}
              step={2}
              nextStepRouterLink={
                laboratory?.checkin_forms?.length
                  ? `${matchUrl}/specialites`
                  : skipSlot
                  ? isCovid
                    ? `${matchUrl}/sidep`
                    : isLoggedIn
                    ? `${matchUrl}/recapitulatif`
                    : `${matchUrl}/connexion`
                  : `${matchUrl}/date`
              }
              isCovid={isCovid}
              isUploadMandatory={!isCovid}
              isLoggedIn={isLoggedIn}
            />
          )}
          exact
        />
        <Route
          path={`${matchUrl}/verification-ordonnance`}
          render={(props) => (
            <UploadAndCheckPrescription
              {...props}
              step={2}
              nextStepRouterLink={
                laboratory?.checkin_forms?.length
                  ? `${matchUrl}/specialites`
                  : skipSlot
                  ? isCovid
                    ? `${matchUrl}/sidep`
                    : isLoggedIn
                    ? `${matchUrl}/recapitulatif`
                    : `${matchUrl}/connexion`
                  : `${matchUrl}/date`
              }
              isCovid={isCovid}
              isUploadMandatory={!isCovid}
            />
          )}
          exact
        />
        <Route
          path={`${matchUrl}/specialites`}
          render={(props) => (
            <SpecialityForms
              {...props}
              step={2}
              nextStepRouterLink={
                skipSlot
                  ? isCovid
                    ? `${matchUrl}/sidep`
                    : isLoggedIn
                    ? `${matchUrl}/recapitulatif`
                    : `${matchUrl}/connexion`
                  : `${matchUrl}/date`
              }
              isCovid={isCovid}
            />
          )}
          exact
        />
        {/* <Route
        path={`${matchUrl}/jeun`}
        render={(props) => (
          <EmptyStomach
            {...props}
            step={3}
            nextStepRouterLink={`${matchUrl}/date`}
          />
        )}
        exact
      /> */}
        <Route
          path={`${matchUrl}/date`}
          render={(props) => (
            <BookingCalendar
              {...props}
              step={3}
              nextStepRouterLink={
                isCovid
                  ? `${matchUrl}/sidep`
                  : isLoggedIn
                  ? `${matchUrl}/recapitulatif`
                  : `${matchUrl}/connexion`
              }
            />
          )}
          exact
        />
        <Route
          path={`${matchUrl}/sidep`}
          strict={false}
          render={(props) => (
            <SidepForm
              {...props}
              step={isSidepOnly ? 0 : 4}
              isSidepOnly={isSidepOnly}
              nextStepRouterLink={
                isSidepOnly
                  ? `${matchUrl}/merci`
                  : isLoggedIn
                  ? `${matchUrl}/recapitulatif`
                  : `${matchUrl}/connexion`
              }
            />
          )}
        />
        <Route
          path={`${matchUrl}/connexion`}
          render={(props) => (
            <RegisterStep
              {...props}
              step={isSidepOnly ? 1 : isCovid ? 5 : 4}
              nextStepRouterLink={`${match.url.replace(
                /\/$/,
                ''
              )}/recapitulatif`}
            />
          )}
        />
        <Route
          path={`${matchUrl}/recapitulatif`}
          render={(props) => (
            <Summary
              {...props}
              step={isSidepOnly ? 2 : isCovid ? 6 : 5}
              nextStepRouterLink={`${matchUrl}/merci`}
              automaticCheckin={automaticCheckin}
            />
          )}
          exact
        />
        <Route
          path={`${matchUrl}/merci`}
          render={(props) => (
            <ThankYou
              {...props}
              step={isSidepOnly ? 3 : isCovid ? 7 : 6}
              lastSelectedLaboratory={lastSelectedLaboratory}
              bodyText={
                isSidepOnly
                  ? 'Nous avons bien reçu vos informations. À bientôt dans les laboratoires Unilabs.'
                  : automaticCheckin
                  ? `Vous serez pris en charge le plus rapidement possible. Veuillez attendre votre prélèvement dans notre salle d'attente.`
                  : undefined
              }
            />
          )}
          exact
        />
        <Redirect
          exact
          from={COVID_APPOINTMENT_ROUTE}
          to={`${COVID_APPOINTMENT_ROUTE}/laboratoire`}
        />
        <Redirect
          exact
          from={APPOINTMENT_ROUTE}
          to={`${APPOINTMENT_ROUTE}/laboratoire`}
        />
        <Redirect
          exact
          from={SIDEP_ONLY_APPOINTMENT_ROUTE}
          to={`${SIDEP_ONLY_APPOINTMENT_ROUTE}/sidep`}
        />
      </IonRouterOutlet>
      <TabBar history={history} className="ion-custom-hide-md-down" />
    </>
  );
};

export interface AppointmentStepProps<T = {}> extends RouteComponentProps<T> {
  step: number;
  nextStepRouterLink?: string;
  stepperComponent?: React.Component<StepperProps>;
}

export default connect(mapStateToProps, null)(AppointmentNavigator);
